// =============================================================================
// SystemCredentials
//
// Admin panel for rotating the Informativ master password.
// Reads all copy from constants -- no hardcoded strings.
// =============================================================================

import { FunctionComponent, useState } from "react";
import { SYSTEM_CREDENTIALS } from "src/constants/adminDashboard";
import { rotateMasterPassword } from "src/utils/admin/informativ";

const SystemCredentials: FunctionComponent = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validate = (): string | null => {
    if (!currentPassword) return SYSTEM_CREDENTIALS.validation.currentPasswordRequired;
    if (!newPassword) return SYSTEM_CREDENTIALS.validation.newPasswordRequired;
    if (newPassword.length < 8) return SYSTEM_CREDENTIALS.validation.passwordTooShort;
    if (!confirmPassword) return SYSTEM_CREDENTIALS.validation.confirmPasswordRequired;
    if (newPassword !== confirmPassword) return SYSTEM_CREDENTIALS.validation.passwordsDoNotMatch;
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await rotateMasterPassword({ currentPassword, newPassword });
      setSuccess(SYSTEM_CREDENTIALS.success);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setError(err?.response?.data?.message || SYSTEM_CREDENTIALS.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">
        {SYSTEM_CREDENTIALS.title}
      </h2>
      <p className="text-sm text-gray-500 mb-6">{SYSTEM_CREDENTIALS.subtitle}</p>

      {error && (
        <p className="text-red-600 text-sm mb-4">{error}</p>
      )}
      {success && (
        <p className="text-green-600 text-sm mb-4">{success}</p>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-md">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            {SYSTEM_CREDENTIALS.form.currentPassword}
          </label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            {SYSTEM_CREDENTIALS.form.newPassword}
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            {SYSTEM_CREDENTIALS.form.confirmPassword}
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-white rounded px-4 py-2 text-sm font-medium self-start disabled:opacity-50"
        >
          {loading ? "Rotating..." : SYSTEM_CREDENTIALS.form.submit}
        </button>
      </form>
    </div>
  );
};

export default SystemCredentials;

